import React from 'react';
import { Box, Container, Typography, Paper, Divider, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const TermsOfService = () => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      py: { xs: 4, md: 8 }
    }}>
      <Container maxWidth="md">
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
              : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,1) 100%)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                  : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Terms of Service
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Last Updated:</strong> October 31, 2025
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Agreement */}
          <Section title="AGREEMENT TO OUR LEGAL TERMS">
            <Typography paragraph>
              We are HypeCircle ('Company', 'we', 'us', or 'our').
            </Typography>
            <Typography paragraph>
              We operate the website{' '}
              <Link href="http://www.hypecircle.co.in" target="_blank" color="primary">
                http://www.hypecircle.co.in
              </Link>
              {' '}(the 'Site'), as well as any other related products and services that refer or link to these legal terms (the 'Legal Terms') (collectively, the 'Services').
            </Typography>
            <Typography paragraph>
              HypeCircle is a social entertainment platform that combines social networking with movie and TV show discovery. Users can create profiles, share posts, follow friends, rate and review content, track their watchlists, earn achievements and badges, compete on leaderboards, and engage in real-time chat. The platform features a unique HypeRating system that tracks trending content based on user engagement and provides personalized recommendations.
            </Typography>
            <Typography paragraph>
              You can contact us by email at{' '}
              <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                hype.circle.main@gmail.com
              </Link>
              {' '}or by mail to __________, Hyderabad, Telangana 500085, India.
            </Typography>
            <Typography paragraph sx={{ fontWeight: 600 }}>
              These Legal Terms constitute a legally binding agreement made between you, whether personally or on behalf of an entity ('you'), and HypeCircle, concerning your access to and use of the Services. You agree that by accessing the Services, you have read, understood, and agreed to be bound by all of these Legal Terms. IF YOU DO NOT AGREE WITH ALL OF THESE LEGAL TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SERVICES AND YOU MUST DISCONTINUE USE IMMEDIATELY.
            </Typography>
            <Typography paragraph>
              The Services are intended for users who are at least 18 years old. Persons under the age of 18 are not permitted to use or register for the Services.
            </Typography>
          </Section>

          {/* Table of Contents */}
          <Section title="TABLE OF CONTENTS">
            <List items={[
              '1. OUR SERVICES',
              '2. INTELLECTUAL PROPERTY RIGHTS',
              '3. USER REPRESENTATIONS',
              '4. USER REGISTRATION',
              '5. PROHIBITED ACTIVITIES',
              '6. USER GENERATED CONTRIBUTIONS',
              '7. CONTRIBUTION LICENCE',
              '8. GUIDELINES FOR REVIEWS',
              '9. SOCIAL MEDIA',
              '10. THIRD-PARTY WEBSITES AND CONTENT',
              '11. SERVICES MANAGEMENT',
              '12. PRIVACY POLICY',
              '13. COPYRIGHT INFRINGEMENTS',
              '14. TERM AND TERMINATION',
              '15. MODIFICATIONS AND INTERRUPTIONS',
              '16. GOVERNING LAW',
              '17. DISPUTE RESOLUTION',
              '18. CORRECTIONS',
              '19. DISCLAIMER',
              '20. LIMITATIONS OF LIABILITY',
              '21. INDEMNIFICATION',
              '22. USER DATA',
              '23. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES',
              '24. CALIFORNIA USERS AND RESIDENTS',
              '25. MISCELLANEOUS',
              '26. CONTACT US'
            ]} />
          </Section>

          {/* 1. Our Services */}
          <Section title="1. OUR SERVICES">
            <Typography paragraph>
              The information provided when using the Services is not intended for distribution to or use by any person or entity in any jurisdiction or country where such distribution or use would be contrary to law or regulation or which would subject us to any registration requirement within such jurisdiction or country.
            </Typography>
            <Typography paragraph>
              The Services are not tailored to comply with industry-specific regulations (Health Insurance Portability and Accountability Act (HIPAA), Federal Information Security Management Act (FISMA), etc.), so if your interactions would be subjected to such laws, you may not use the Services. You may not use the Services in a way that would violate the Gramm-Leach-Bliley Act (GLBA).
            </Typography>
          </Section>

          {/* 2. Intellectual Property */}
          <Section title="2. INTELLECTUAL PROPERTY RIGHTS">
            <SubSection title="Our intellectual property">
              <Typography paragraph>
                We are the owner or the licensee of all intellectual property rights in our Services, including all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics in the Services (collectively, the 'Content'), as well as the trademarks, service marks, and logos contained therein (the 'Marks').
              </Typography>
              <Typography paragraph>
                The Content and Marks are provided in or through the Services 'AS IS' for your personal, non-commercial use or internal business purpose only.
              </Typography>
            </SubSection>

            <SubSection title="Your use of our Services">
              <Typography paragraph>
                Subject to your compliance with these Legal Terms, we grant you a non-exclusive, non-transferable, revocable licence to access the Services and download or print a copy of any portion of the Content to which you have properly gained access, solely for your personal, non-commercial use or internal business purpose.
              </Typography>
            </SubSection>

            <SubSection title="Your submissions and contributions">
              <Typography paragraph>
                By directly sending us any question, comment, suggestion, idea, feedback, or other information about the Services ('Submissions'), you agree to assign to us all intellectual property rights in such Submission.
              </Typography>
              <Typography paragraph>
                When you post Contributions, you grant us an unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and licence to use, copy, reproduce, distribute, sell, resell, publish, broadcast, retitle, store, publicly perform, publicly display, reformat, translate, excerpt (in whole or in part), and exploit your Contributions for any purpose, commercial, advertising, or otherwise.
              </Typography>
            </SubSection>
          </Section>

          {/* 3. User Representations */}
          <Section title="3. USER REPRESENTATIONS">
            <Typography paragraph>
              By using the Services, you represent and warrant that:
            </Typography>
            <List items={[
              'All registration information you submit will be true, accurate, current, and complete',
              'You will maintain the accuracy of such information and promptly update such registration information as necessary',
              'You have the legal capacity and you agree to comply with these Legal Terms',
              'You are not a minor in the jurisdiction in which you reside',
              'You will not access the Services through automated or non-human means',
              'You will not use the Services for any illegal or unauthorised purpose',
              'Your use of the Services will not violate any applicable law or regulation'
            ]} />
          </Section>

          {/* 4. User Registration */}
          <Section title="4. USER REGISTRATION">
            <Typography paragraph>
              You may be required to register to use the Services. You agree to keep your password confidential and will be responsible for all use of your account and password. We reserve the right to remove, reclaim, or change a username you select if we determine, in our sole discretion, that such username is inappropriate, obscene, or otherwise objectionable.
            </Typography>
          </Section>

          {/* 5. Prohibited Activities */}
          <Section title="5. PROHIBITED ACTIVITIES">
            <Typography paragraph>
              You may not access or use the Services for any purpose other than that for which we make the Services available. As a user of the Services, you agree not to:
            </Typography>
            <List items={[
              'Systematically retrieve data or other content from the Services to create or compile a collection, compilation, database, or directory without written permission from us',
              'Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords',
              'Circumvent, disable, or otherwise interfere with security-related features of the Services',
              'Disparage, tarnish, or otherwise harm, in our opinion, us and/or the Services',
              'Use any information obtained from the Services in order to harass, abuse, or harm another person',
              'Make improper use of our support services or submit false reports of abuse or misconduct',
              'Use the Services in a manner inconsistent with any applicable laws or regulations',
              'Engage in unauthorised framing of or linking to the Services',
              'Upload or transmit viruses, Trojan horses, or other material that interferes with any party\'s uninterrupted use and enjoyment of the Services',
              'Engage in any automated use of the system, such as using scripts to send comments or messages',
              'Delete the copyright or other proprietary rights notice from any Content',
              'Attempt to impersonate another user or person or use the username of another user',
              'Interfere with, disrupt, or create an undue burden on the Services or the networks or services connected to the Services',
              'Harass, annoy, intimidate, or threaten any of our employees or agents engaged in providing any portion of the Services to you',
              'Copy or adapt the Services\' software, including but not limited to Flash, PHP, HTML, JavaScript, or other code',
              'Use the Services as part of any effort to compete with us or otherwise use the Services for any revenue-generating endeavour or commercial enterprise',
              'Sell or otherwise transfer your profile',
              'Manipulate or artificially inflate HypeRating scores, achievements, badges, or leaderboard rankings',
              'Create multiple accounts to circumvent bans, restrictions, or achievement systems',
              'Post false, misleading, or fraudulent movie/TV show reviews or ratings',
              'Use automated bots, scripts, or tools to interact with the Services without authorization',
              'Harass, bully, or threaten other users through posts, comments, or chat features',
              'Share or distribute copyrighted content (movies, TV shows, images) without proper authorization'
            ]} />
          </Section>

          {/* 6. User Generated Contributions */}
          <Section title="6. USER GENERATED CONTRIBUTIONS">
            <Typography paragraph>
              The Services may invite you to chat, contribute to, or participate in blogs, message boards, online forums, and other functionality. When you create or make available any Contributions, you thereby represent and warrant that:
            </Typography>
            <List items={[
              'You are the creator and owner of or have the necessary licences, rights, consents, releases, and permissions to use your Contributions',
              'Your Contributions are not false, inaccurate, or misleading',
              'Your Contributions are not unsolicited or unauthorised advertising, promotional materials, pyramid schemes, chain letters, spam, mass mailings, or other forms of solicitation',
              'Your Contributions are not obscene, lewd, lascivious, filthy, violent, harassing, libellous, slanderous, or otherwise objectionable',
              'Your Contributions do not ridicule, mock, disparage, intimidate, or abuse anyone',
              'Your Contributions do not violate any applicable law, regulation, or rule',
              'Your Contributions do not violate the privacy or publicity rights of any third party',
              'Your Contributions do not include any offensive comments that are connected to race, national origin, gender, sexual preference, or physical handicap'
            ]} />
          </Section>

          {/* 7. Contribution Licence */}
          <Section title="7. CONTRIBUTION LICENCE">
            <Typography paragraph>
              By posting your Contributions to any part of the Services, you automatically grant us an unrestricted, unlimited, irrevocable, perpetual, non-exclusive, transferable, royalty-free, fully-paid, worldwide right, and licence to host, use, copy, reproduce, disclose, sell, resell, publish, broadcast, retitle, archive, store, cache, publicly perform, publicly display, reformat, translate, transmit, excerpt, and distribute such Contributions for any purpose, commercial, advertising, or otherwise.
            </Typography>
            <Typography paragraph>
              We do not assert any ownership over your Contributions. You retain full ownership of all of your Contributions and any intellectual property rights or other proprietary rights associated with your Contributions.
            </Typography>
          </Section>

          {/* 8. Guidelines for Reviews */}
          <Section title="8. GUIDELINES FOR REVIEWS">
            <Typography paragraph>
              We may provide you areas on the Services to leave reviews or ratings. When posting a review, you must comply with the following criteria:
            </Typography>
            <List items={[
              'You should have firsthand experience with the person/entity being reviewed',
              'Your reviews should not contain offensive profanity, or abusive, racist, offensive, or hateful language',
              'Your reviews should not contain discriminatory references based on religion, race, gender, national origin, age, marital status, sexual orientation, or disability',
              'Your reviews should not contain references to illegal activity',
              'You should not be affiliated with competitors if posting negative reviews',
              'You may not post any false or misleading statements',
              'You may not organise a campaign encouraging others to post reviews, whether positive or negative'
            ]} />
          </Section>

          {/* 9. Social Media */}
          <Section title="9. SOCIAL MEDIA">
            <Typography paragraph>
              As part of the functionality of the Services, you may link your account with online accounts you have with third-party service providers (each such account, a 'Third-Party Account') by either providing your Third-Party Account login information through the Services or allowing us to access your Third-Party Account.
            </Typography>
            <Typography paragraph>
              By granting us access to any Third-Party Accounts, you understand that we may access, make available, and store any content that you have provided to and stored in your Third-Party Account so that it is available on and through the Services via your account.
            </Typography>
          </Section>

          {/* 10. Third-Party Websites */}
          <Section title="10. THIRD-PARTY WEBSITES AND CONTENT">
            <Typography paragraph>
              The Services may contain links to other websites ('Third-Party Websites') as well as articles, photographs, text, graphics, pictures, designs, music, sound, video, information, applications, software, and other content or items belonging to or originating from third parties ('Third-Party Content').
            </Typography>
            <Typography paragraph>
              We are not responsible for any Third-Party Websites accessed through the Services or any Third-Party Content posted on, available through, or installed from the Services. If you decide to leave the Services and access the Third-Party Websites, you do so at your own risk.
            </Typography>
          </Section>

          {/* 11. Services Management */}
          <Section title="11. SERVICES MANAGEMENT">
            <Typography paragraph>
              We reserve the right, but not the obligation, to:
            </Typography>
            <List items={[
              'Monitor the Services for violations of these Legal Terms',
              'Take appropriate legal action against anyone who violates the law or these Legal Terms',
              'Refuse, restrict access to, limit the availability of, or disable any of your Contributions',
              'Remove from the Services or otherwise disable all files and content that are excessive in size or burdensome to our systems',
              'Otherwise manage the Services in a manner designed to protect our rights and property'
            ]} />
          </Section>

          {/* 12. Privacy Policy */}
          <Section title="12. PRIVACY POLICY">
            <Typography paragraph>
              We care about data privacy and security. Please review our Privacy Policy:{' '}
              <Link href="http://www.hypecircle.co.in/privacy" target="_blank" color="primary">
                http://www.hypecircle.co.in/privacy
              </Link>
              . By using the Services, you agree to be bound by our Privacy Policy.
            </Typography>
            <Typography paragraph>
              Please be advised the Services are hosted in India and United States. If you access the Services from any other region of the world with laws governing personal data collection, use, or disclosure that differ from applicable laws in India and United States, then through your continued use of the Services, you are transferring your data to India and United States, and you expressly consent to have your data transferred to and processed in India and United States.
            </Typography>
          </Section>

          {/* 13. Copyright Infringements */}
          <Section title="13. COPYRIGHT INFRINGEMENTS">
            <Typography paragraph>
              We respect the intellectual property rights of others. If you believe that any material available on or through the Services infringes upon any copyright you own or control, please immediately notify us using the contact information provided below.
            </Typography>
          </Section>

          {/* 14. Term and Termination */}
          <Section title="14. TERM AND TERMINATION">
            <Typography paragraph sx={{ fontWeight: 600 }}>
              WITHOUT LIMITING ANY OTHER PROVISION OF THESE LEGAL TERMS, WE RESERVE THE RIGHT TO, IN OUR SOLE DISCRETION AND WITHOUT NOTICE OR LIABILITY, DENY ACCESS TO AND USE OF THE SERVICES TO ANY PERSON FOR ANY REASON OR FOR NO REASON, INCLUDING WITHOUT LIMITATION FOR BREACH OF ANY REPRESENTATION, WARRANTY, OR COVENANT CONTAINED IN THESE LEGAL TERMS OR OF ANY APPLICABLE LAW OR REGULATION.
            </Typography>
            <Typography paragraph>
              If we terminate or suspend your account for any reason, you are prohibited from registering and creating a new account under your name, a fake or borrowed name, or the name of any third party.
            </Typography>
          </Section>

          {/* 15. Modifications */}
          <Section title="15. MODIFICATIONS AND INTERRUPTIONS">
            <Typography paragraph>
              We reserve the right to change, modify, or remove the contents of the Services at any time or for any reason at our sole discretion without notice. We cannot guarantee the Services will be available at all times.
            </Typography>
          </Section>

          {/* 16. Governing Law */}
          <Section title="16. GOVERNING LAW">
            <Typography paragraph>
              These Legal Terms shall be governed by and defined following the laws of India. HypeCircle and yourself irrevocably consent that the courts of India shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these Legal Terms.
            </Typography>
          </Section>

          {/* 17. Dispute Resolution */}
          <Section title="17. DISPUTE RESOLUTION">
            <SubSection title="Informal Negotiations">
              <Typography paragraph>
                To expedite resolution and control the cost of any dispute, the Parties agree to first attempt to negotiate any Dispute informally for at least thirty (30) days before initiating arbitration.
              </Typography>
            </SubSection>

            <SubSection title="Binding Arbitration">
              <Typography paragraph>
                Any dispute arising out of or in connection with these Legal Terms shall be referred to and finally resolved by the International Commercial Arbitration Court under the European Arbitration Chamber (Belgium, Brussels, Avenue Louise, 146). The number of arbitrators shall be five (5). The seat of arbitration shall be Hyderabad, India. The language of the proceedings shall be English.
              </Typography>
            </SubSection>
          </Section>

          {/* 18. Corrections */}
          <Section title="18. CORRECTIONS">
            <Typography paragraph>
              There may be information on the Services that contains typographical errors, inaccuracies, or omissions. We reserve the right to correct any errors, inaccuracies, or omissions and to change or update the information on the Services at any time, without prior notice.
            </Typography>
          </Section>

          {/* 19. Disclaimer */}
          <Section title="19. DISCLAIMER">
            <Typography paragraph sx={{ fontWeight: 600 }}>
              THE SERVICES ARE PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SERVICES AND YOUR USE THEREOF.
            </Typography>
          </Section>

          {/* 20. Limitations of Liability */}
          <Section title="20. LIMITATIONS OF LIABILITY">
            <Typography paragraph sx={{ fontWeight: 600 }}>
              IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES. NOTWITHSTANDING ANYTHING TO THE CONTRARY CONTAINED HEREIN, OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER WILL AT ALL TIMES BE LIMITED TO THE LESSER OF THE AMOUNT PAID, IF ANY, BY YOU TO US OR ₹2,000 INR.
            </Typography>
          </Section>

          {/* 21. Indemnification */}
          <Section title="21. INDEMNIFICATION">
            <Typography paragraph>
              You agree to defend, indemnify, and hold us harmless from and against any loss, damage, liability, claim, or demand made by any third party due to or arising out of:
            </Typography>
            <List items={[
              'Your Contributions',
              'Use of the Services',
              'Breach of these Legal Terms',
              'Any breach of your representations and warranties',
              'Your violation of the rights of a third party, including but not limited to intellectual property rights',
              'Any overt harmful act toward any other user of the Services'
            ]} />
          </Section>

          {/* 22. User Data */}
          <Section title="22. USER DATA">
            <Typography paragraph>
              We will maintain certain data that you transmit to the Services for the purpose of managing the performance of the Services. You are solely responsible for all data that you transmit or that relates to any activity you have undertaken using the Services. You agree that we shall have no liability to you for any loss or corruption of any such data.
            </Typography>
          </Section>

          {/* 23. Electronic Communications */}
          <Section title="23. ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES">
            <Typography paragraph>
              Visiting the Services, sending us emails, and completing online forms constitute electronic communications. You consent to receive electronic communications, and you agree that all agreements, notices, disclosures, and other communications we provide to you electronically satisfy any legal requirement that such communication be in writing.
            </Typography>
          </Section>

          {/* 24. California Users */}
          <Section title="24. CALIFORNIA USERS AND RESIDENTS">
            <Typography paragraph>
              If any complaint with us is not satisfactorily resolved, you can contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 1625 North Market Blvd., Suite N 112, Sacramento, California 95834 or by telephone at (800) 952-5210 or (916) 445-1254.
            </Typography>
          </Section>

          {/* 25. Miscellaneous */}
          <Section title="25. MISCELLANEOUS">
            <Typography paragraph>
              These Legal Terms and any policies or operating rules posted by us on the Services constitute the entire agreement and understanding between you and us. Our failure to exercise or enforce any right or provision of these Legal Terms shall not operate as a waiver of such right or provision.
            </Typography>
          </Section>

          {/* 26. Contact Us */}
          <Section title="26. CONTACT US">
            <Typography paragraph>
              In order to resolve a complaint regarding the Services or to receive further information regarding use of the Services, please contact us at:
            </Typography>
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              bgcolor: 'action.hover', 
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="body1" gutterBottom>
                <strong>HypeCircle</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                Hyderabad, Telangana 500085
              </Typography>
              <Typography variant="body1" gutterBottom>
                India
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                <strong>Email:</strong>{' '}
                <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                  hype.circle.main@gmail.com
                </Link>
              </Typography>
            </Box>
          </Section>

          {/* Footer */}
          <Divider sx={{ my: 4 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} HypeCircle. All rights reserved.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              These Terms of Service are effective as of October 31, 2025
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// Helper Components
const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography 
      variant="h5" 
      component="h2" 
      gutterBottom 
      sx={{ 
        fontWeight: 600,
        mb: 2,
        color: 'primary.main'
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ mb: 3, ml: { xs: 0, md: 2 } }}>
    <Typography 
      variant="h6" 
      component="h3" 
      gutterBottom 
      sx={{ 
        fontWeight: 500,
        mb: 1.5,
        fontSize: '1.1rem'
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const List = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mt: 1 }}>
    {items.map((item, index) => (
      <Typography 
        key={index} 
        component="li" 
        paragraph 
        sx={{ mb: 1 }}
      >
        {item}
      </Typography>
    ))}
  </Box>
);

export default TermsOfService;
